import { useAuth } from "@/src/contexts/auth.context";
import type { RecognitionAnswers } from "@/src/models/rewardly.model";
import { InvitableMember } from "@/src/models/user.model";
import {
	useCreateStarTransaction,
	useGetCurrentActiveSeason,
	useGetStarTransactionsInfinite,
	useGetUserStarBalance,
	useUpdateUserStarBalance,
} from "@/src/services/api/rewardly/rewardly.hooks";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";

/** Reference design formats rewards as e.g. "$75.00". */
export const formatReward = (stars: number, monetaryValue?: number) =>
	`$${(stars * (monetaryValue ?? 1)).toFixed(2)}`;

/**
 * Build a human-readable `message` string from structured answers.
 * Used as the backward-compatible display path on all records.
 */
export const buildRecognitionMessage = (answers: RecognitionAnswers): string => {
	const parts: string[] = [];
	if (answers.leadership?.trim()) parts.push(`Leadership: ${answers.leadership.trim()}`);
	if (answers.reliability?.trim()) parts.push(`Reliability: ${answers.reliability.trim()}`);
	if (answers.collaboration?.trim()) parts.push(`Collaboration: ${answers.collaboration.trim()}`);
	if (answers.impact?.trim()) parts.push(`Impact: ${answers.impact.trim()}`);
	return parts.join("\n\n");
};

/**
 * Strip empty/whitespace-only fields from answers so unanswered questions
 * are omitted rather than stored as empty strings (AC-B1-05-08).
 */
export const cleanAnswers = (answers: RecognitionAnswers): RecognitionAnswers => {
	const result: RecognitionAnswers = {};
	if (answers.leadership?.trim()) result.leadership = answers.leadership.trim();
	if (answers.reliability?.trim()) result.reliability = answers.reliability.trim();
	if (answers.collaboration?.trim()) result.collaboration = answers.collaboration.trim();
	if (answers.impact?.trim()) result.impact = answers.impact.trim();
	return result;
};

export const useMyStars = () => {
	const { t } = useTranslation();
	const { user, orgId } = useAuth();
	const { mutateAsync: createStarTransaction, isPending: isSending } = useCreateStarTransaction();
	const { mutateAsync: updateBalance } = useUpdateUserStarBalance();
	const { data: seasonData, isLoading: isSeasonLoading } = useGetCurrentActiveSeason(orgId ?? "");
	const currentSeason = seasonData?.getseasons?.items?.[0];

	const { data: transactionsData, isLoading: isTransactionsLoading, refetch } =
		useGetStarTransactionsInfinite(currentSeason?.ItemId ?? "");

	const { data: seasonBalance } = useGetUserStarBalance(user?.itemId ?? "", currentSeason?.ItemId ?? "")
	const seasonAllowance = seasonBalance?.getuserStarBalances?.items?.[0]?.starsRemaining ?? 0;

	const starsReceived = useMemo(() => {
		const allTransactions = transactionsData?.pages.flatMap(
			page => page?.getstarTransactions?.items ?? []
		) ?? [];
		return allTransactions
			.filter(t => t.recipientId === user?.itemId)
			.reduce((sum, t) => sum + (t.amount ?? 0), 0);
	}, [transactionsData, user?.itemId]);

	const rewardValue = formatReward(starsReceived, currentSeason?.monetaryValue);

	const isLoading = isSeasonLoading || isTransactionsLoading;
	const [star, setStars] = useState("");
	/** CR-B1-05: four structured recognition answers replace the single reason field. */
	const [answers, setAnswers] = useState<RecognitionAnswers>({});
	const [usersModalVisible, setUsersModalVisible] = useState(false);
	const [selectedUsers, setSelectedUsers] = useState<InvitableMember[]>([]);

	const [showStarBurstAnimation, setShowStarBurstAnimation] = useState(false);

	const setAnswer = (key: keyof RecognitionAnswers, value: string) =>
		setAnswers(prev => ({ ...prev, [key]: value }));

	const removeUser = (user: InvitableMember) =>
		setSelectedUsers(prev => prev.filter(u => u.itemId !== user.itemId));

	const validateSend = (): string | null => {
		if (selectedUsers.length === 0) return t("ERROR_SELECT_RECIPIENT");
		const amount = Number(star);
		if (!star || isNaN(amount) || amount <= 0) return t("ERROR_INVALID_STAR_AMOUNT");
		const min = currentSeason?.minGiveAmt;
		const max = currentSeason?.maxGiveAmt;
		if ((min != null && amount < min) || (max != null && amount > max)) {
			return t("ERROR_STARS_RANGE", { min: min ?? 1, max: max ?? amount });
		}
		// CR-B1-05: at least one answer required (replaces the old single-reason check)
		const hasAtLeastOneAnswer = Object.values(answers).some(v => v?.trim());
		if (!hasAtLeastOneAnswer) return t("ERROR_AT_LEAST_ONE_RECOGNITION_ANSWER");
		if (amount * selectedUsers.length > seasonAllowance) {
			return t("ERROR_INSUFFICIENT_ALLOWANCE");
		}
		return null;
	};

	const handleSendStars = async () => {
		if (!currentSeason?.ItemId || !user?.itemId) return;

		const validationError = validateSend();
		if (validationError) {
			Toast.show({ type: "error", text1: validationError });
			return;
		}

		const totalStarsToSend = Number(star) * selectedUsers.length;
		const structured = cleanAnswers(answers);
		// message is always populated for backward-compatible display (AC-B1-05-04)
		const message = buildRecognitionMessage(answers);

		try {
			const transactions = selectedUsers.map(recipient =>
				createStarTransaction({
					seasonId: currentSeason.ItemId,
					senderId: user.itemId,
					recipientId: recipient.itemId,
					senderInfo: {
						userId: user.itemId,
						email: user.email,
						displayName: `${user.firstName} ${user.lastName}`,
						profileImageUrl: user.profileImageUrl as string,
					},
					recipientInfo: {
						userId: recipient.itemId,
						email: recipient.email,
						displayName: `${recipient.firstName} ${recipient.lastName}`,
						profileImageUrl: recipient.profileImageUrl as string,
					},
					amount: Number(star),
					message,
					// CR-B1-05: structured per-question answers (schema-gap guarded)
					recognitionAnswers: Object.keys(structured).length > 0 ? structured : undefined,
					type: "recognition",
				})
			);

			const results = await Promise.allSettled(transactions);
			const failures = results.filter(r => r.status === "rejected");

			if (failures.length > 0) {
				Toast.show({ type: "error", text1: t("ERROR_STARS_SEND_FAILED") });
				return;
			}

			await updateBalance({
				userId: user.itemId,
				seasonId: currentSeason.ItemId,
				starsRemaining: seasonAllowance - totalStarsToSend,
			});

			setStars("");
			setAnswers({});
			setSelectedUsers([]);
			setUsersModalVisible(false);
			setShowStarBurstAnimation(true);
			Toast.show({ type: "success", text1: t("MSG_STARS_SENT_SUCCESS") });
			refetch(); // refresh transactions so derived values update
		} catch {
			Toast.show({ type: "error", text1: t("ERROR_STARS_SEND_FAILED") });
		}
	};

	return {
		currentSeason,
		seasonAllowance,
		starsReceived,
		rewardValue,
		isLoading,
		isSending,
		star, setStars,
		answers, setAnswer,
		selectedUsers, setSelectedUsers,
		usersModalVisible, setUsersModalVisible,
		removeUser,
		handleSendStars,
		showStarBurstAnimation,
		setShowStarBurstAnimation
	};
};
